import PropTypes from 'prop-types';
// @mui
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import TableHead from '@mui/material/TableHead';
import TableCell from '@mui/material/TableCell';
import TableSortLabel from '@mui/material/TableSortLabel';

// ----------------------------------------------------------------------

const visuallyHidden = {
  border: 0,
  margin: -1,
  padding: 0,
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  position: 'absolute',
  whiteSpace: 'nowrap',
  clip: 'rect(0 0 0 0)',
};

// ----------------------------------------------------------------------

export default function TableHeadCustom({
  order,
  orderBy,
  rowCount = 0,
  headLabel = [],          // ✅ FIX: default value
  numSelected = 0,
  onSort,
  onSelectAllRows,
  sx,
}) {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const bgColor = isLight ? '#E9ECEF' : '#2f3944';

  return (
    <TableHead sx={{ ...sx, minHeight: '48px !important' }}>
      <TableRow>
        {onSelectAllRows && (
          <TableCell padding="checkbox">
            <Checkbox
              indeterminate={!!numSelected && numSelected < rowCount}
              checked={!!rowCount && numSelected === rowCount}
              onChange={(event) => onSelectAllRows(event.target.checked)}
            />
          </TableCell>
        )}

        {/* ✅ SAFE MAP */}
        {headLabel.length > 0 &&
          headLabel.map((headCell, index) => {
            const isLastColumn = index === headLabel.length - 1;
            const computedLabel = headCell.label || (isLastColumn ? 'Actions' : '');
            const isActionColumn = computedLabel === 'Actions' && isLastColumn;
            const isSticky =
              headCell.position === 'sticky' || isLastColumn;

            return (
              <TableCell
                key={headCell.id}
                align={headCell.align || (isActionColumn ? 'center' : 'left')}
                sortDirection={orderBy === headCell.id ? order : false}
                sx={{
                  width: headCell.width,
                  minWidth: headCell.minWidth,
                  fontWeight: 600,
                  fontSize: '12px !important',
                  color: theme.palette.mode === 'light'
                    ? '#374151'
                    : '#E5E7EB',
                  position: isSticky ? 'sticky' : 'static',
                  left:
                    headCell.position === 'sticky' && !isLastColumn
                      ? 0
                      : 'auto',
                  right: isLastColumn ? 0 : 'auto',
                  zIndex: isSticky ? 3 : 'auto',
                  backgroundColor: bgColor,
                  borderBottom: theme.palette.mode === 'light'
                    ? '2px solid #D1D5DB'
                    : '2px solid #374151',
                  boxShadow: isLastColumn
                    ? '-2px 0 5px rgba(0,0,0,0.05)'
                    : 'none',
                }}
              >
                {onSort && !isActionColumn ? (
                  <TableSortLabel
                    hideSortIcon
                    active={orderBy === headCell.id}
                    direction={orderBy === headCell.id ? order : 'asc'}
                    onClick={() => onSort(headCell.id)}
                  >
                    {computedLabel}

                    {orderBy === headCell.id && (
                      <Box sx={visuallyHidden}>
                        {order === 'desc'
                          ? 'sorted descending'
                          : 'sorted ascending'}
                      </Box>
                    )}
                  </TableSortLabel>
                ) : (
                  computedLabel
                )}
              </TableCell>
            );
          })}
      </TableRow>
    </TableHead>
  );
}

// ----------------------------------------------------------------------

TableHeadCustom.propTypes = {
  sx: PropTypes.object,
  onSort: PropTypes.func,
  orderBy: PropTypes.string,
  headLabel: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      align: PropTypes.string,
      label: PropTypes.string.isRequired,
      width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      position: PropTypes.oneOf(['static', 'sticky']),
    })
  ),
  rowCount: PropTypes.number,
  numSelected: PropTypes.number,
  onSelectAllRows: PropTypes.func,
  order: PropTypes.oneOf(['asc', 'desc']),
};